import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Store } from '@sama/app-state';
import { assert, isDefined } from '@sama/js-core';
import { InjectableSuperclass } from '@sama/ng-core';
import { SamaSkeletonLoaderModule } from '@sama/ui-library';
import { Change, diffWords } from 'diff';
import {
  BehaviorSubject,
  combineLatest,
  debounce,
  Observable,
  race,
  shareReplay,
  Subject,
  timer,
} from 'rxjs';
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators';
import {
  isTextObjectState,
  ObjectState,
} from '../../../../cross-team-shared/annotation/shared/state/object-state';
import { getOrThrowStore } from '../../../../cross-team-shared/misc-utils/assert-store';
import { getOrThrow } from '../../../../cross-team-shared/misc-utils/strict-record';
import { delayOnMicrotaskQueue } from '../../../../shared/misc-utilities/misc-utils';
import { TaskWorkspaceStore } from '../../../h3ml/state/task-workspace-store';
import { SubmissionLoadingService } from '../../../http/loading/submission-loading.service';
import { SamplingWorkspaceStore } from '../../../sampling-workspace/state/sampling-workspace-store';
import { RootUndoService } from '../../../shared/misc-services/root-undo.service';
import { getWordCount } from '../../services/word-count';
import { HighlightResponseComponent } from '../highlight-response/highlight-response.component';
import { SelectableObjectComponent } from '../selectable-object/selectable-object.component';

type ReferenceTextVersion = 'Original' | 'Latest';

@Component({
  selector: 'h3-response-editor',
  templateUrl: './response-editor.component.html',
  styleUrl: './response-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SelectableObjectComponent,
    CommonModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatSlideToggleModule,
    MatButtonToggleModule,
    SamaSkeletonLoaderModule,
    HighlightResponseComponent,
  ],
})
export class ResponseEditorComponent
  extends InjectableSuperclass
  implements OnInit
{
  @Input({ required: true }) responseObject!: ObjectState;

  private readonly store = inject(TaskWorkspaceStore);
  private readonly submissionLoadingService = inject(SubmissionLoadingService);

  canSelectLatestVersion =
    !!this.store.state().task.submissions?.length &&
    this.store.state().workspace !== 'sampling';
  isReadOnly = this.store.state().readOnly;
  isSampling$ = this.store('workspace').$.pipe(
    map((workspace) => workspace === 'sampling'),
  );
  hasSampFinalized$ = inject(SamplingWorkspaceStore).hasSampleFinalized$();
  editorText$!: Observable<string>;
  referenceText$!: Observable<string | undefined>;

  isCollapsed$ = new BehaviorSubject(true);
  selectedVersion$ = new BehaviorSubject<ReferenceTextVersion>('Original');
  isCompareEnabled$ = new BehaviorSubject(false);
  diff$!: Observable<Change[]>;
  percentageOfChange$!: Observable<number>;
  textAreaFocusOut$ = new Subject<void>();

  private rootUndoService = inject(RootUndoService);
  private responseUpdate$ = new Subject<void>();
  private responseTextStore!: Store<string>;
  private textObjectIndex!: number;

  constructor() {
    super();

    if (this.canSelectLatestVersion) {
      this.lazyLoadLatestSubmission();
    }
  }

  setUpPushCurrentStateSubscription() {
    this.subscribeTo(
      this.responseUpdate$.pipe(
        debounce(() => race(timer(2000), this.textAreaFocusOut$)),
        // need to delay on microtask queue to make sure the state is pushed
        // before unsubscribing
        takeUntil(this.textAreaFocusOut$.pipe(delayOnMicrotaskQueue())),
      ),
      () => this.rootUndoService.pushCurrentState(),
    );
  }

  ngOnInit() {
    const { objects } = this.store.state();
    const textObjectIndex = this.responseObject.childObjectIds.find((id) =>
      isTextObjectState(getOrThrow(objects, id)),
    );
    assert(isDefined(textObjectIndex), 'Response should have a text object');
    this.textObjectIndex = textObjectIndex;
    this.responseTextStore = getOrThrowStore(
      this.store('objects'),
      textObjectIndex,
    )('tags')('text');

    this.editorText$ = this.responseTextStore.$;
    this.referenceText$ = this.buildReferenceText$();
    this.diff$ = this.buildDiff$();
    this.percentageOfChange$ = this.buildPercentageOfChange$();

    if (!this.isReadOnly) {
      this.subscribeTo(
        this.store('highlightState')('selected').$,
        (selectedHighlight) => {
          if (selectedHighlight?.objectId === this.textObjectIndex) {
            this.isCompareEnabled$.next(false);
            this.selectedVersion$.next('Latest');
            this.isCollapsed$.next(false);
          }
        },
      );
    }
  }

  toggleCollapse() {
    this.isCollapsed$.next(!this.isCollapsed$.value);
  }

  updateText(event: Event) {
    assert(
      event.target instanceof HTMLTextAreaElement,
      'Update text event should come from a textarea',
    );
    const text = event.target.value;
    this.responseTextStore.set(text);
    this.responseUpdate$.next();
  }

  private buildReferenceText$() {
    return this.selectedVersion$.pipe(
      switchMap(
        (selectedVersion) =>
          this.store('submissions')(
            selectedVersion === 'Original' ? -1 : this.getLatestSubmissionId(),
          ).$,
      ),
      map(
        (submissionState) =>
          submissionState &&
          (submissionState.objects[this.textObjectIndex]?.tags.text ?? ''),
      ),
    );
  }

  private buildDiff$() {
    return combineLatest([this.editorText$, this.referenceText$]).pipe(
      filter(([_editorText, referenceText]) => isDefined(referenceText)),
      map(([editorText, referenceText]) =>
        diffWords(referenceText!, editorText),
      ),
      shareReplay({ refCount: true }),
    );
  }

  private buildPercentageOfChange$() {
    return combineLatest([this.diff$, this.referenceText$]).pipe(
      map(([diff, referenceText]) => {
        if (diff.length === 0) {
          return 0;
        }

        const referenceTextWordCount = referenceText
          ? getWordCount(referenceText)
          : 0;
        const changedWordCount = diff
          .filter((diffPart) => diffPart.added || diffPart.removed)
          .reduce((count, diffPart) => count + getWordCount(diffPart.value), 0);
        const changedAndReferenceWordCount =
          changedWordCount + referenceTextWordCount;
        return changedAndReferenceWordCount === 0
          ? 0
          : changedWordCount / changedAndReferenceWordCount;
      }),
    );
  }

  private getLatestSubmissionId() {
    const { selectedSubmissionId, task, workspace } = this.store.state();
    assert(task.submissions, 'task should have submissions');

    if (isDefined(selectedSubmissionId)) {
      const submissionIndex = task.submissions.findIndex(
        (submission) => submission.id === selectedSubmissionId,
      );
      return task.submissions[Math.max(0, submissionIndex - 1)].id;
    } else if (workspace === 'readOnly' && task.submissions.length >= 2) {
      return task.submissions[task.submissions.length - 2].id;
    }

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- can be undefined if there are no submissions
    const submissionId = this.store.getLastSubmission()?.id;
    assert(
      isDefined(submissionId),
      'when lastSubmission version is selected, there should always be a previous submission',
    );
    return submissionId;
  }

  private lazyLoadLatestSubmission() {
    const submissionId = this.getLatestSubmissionId();

    const isSubmissionInitialized$ = this.store('submissions')(
      submissionId,
    ).$.pipe(filter(isDefined));

    this.subscribeTo(
      this.selectedVersion$.pipe(
        filter((selectedVersion) => selectedVersion === 'Latest'),
        take(1),
        takeUntil(isSubmissionInitialized$),
      ),
      () => this.submissionLoadingService.load(submissionId),
    );
  }
}
